import { useCallback, useState, useEffect, useRef } from 'react';
import { AuthLayoutContainer } from 'components/Layouts/AuthLayout';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, colors, Input, Button, Alert, IconGraphy } from '@leapeasy/ui-kit';
import { useNavigate, useLocation } from 'react-router-dom';
import { syncPostLoginReturnPathFromLocationSearch } from 'utils/postLoginReturnPath';
import { useAuthSession } from 'hooks/useAuthSession';
import { Box } from '@mui/material';
import validator from 'validator';
import { Otp } from './Otp';
import {
  getCaptchaStatus,
  refreshUser,
  logInStart,
  logOut,
  setError,
} from 'store/actions/authActions';
import ReCAPTCHA from 'react-google-recaptcha';

export const Login = () => {
  const dispatch = useDispatch();

  const requireCaptcha = useSelector((state) => state.getIn(['auth', 'requireCaptcha']));
  const loading = useSelector((state) => state.getIn(['auth', 'loading']));
  const error = useSelector((state) => state.getIn(['auth', 'error']));

  const { token, user: currentUser, isPendingLogin2FA } = useAuthSession();

  useEffect(() => {
    dispatch(setError(''));
    dispatch(getCaptchaStatus());
  }, []);

  const successMsg = useSelector((state) => state.getIn(['notification', 'successMsg']));

  const navigate = useNavigate();
  const location = useLocation();
  const inviteAcceptedMessage = location.state?.message;
  const inviteAcceptedEmail = location.state?.email;
  const captchaEl = useRef();
  const [captcha, setCaptcha] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    syncPostLoginReturnPathFromLocationSearch(location.search);
  }, [location.search]);

  useEffect(() => {
    if (inviteAcceptedEmail) {
      setEmail(inviteAcceptedEmail);
    }
  }, [inviteAcceptedEmail]);

  const normalizedEmail = email.trim();
  const isEmailValid = validator.isEmail(normalizedEmail);

  const onForgetPassword = useCallback(() => {
    navigate('/reset-password');
  }, []);

  const onClickLogin = useCallback(() => {
    const trimmedEmail = normalizedEmail;
    const payload = {
      email: trimmedEmail,
      password,
      captcha_token: captcha,
    };

    dispatch(logInStart(payload));
  }, [dispatch, normalizedEmail, password, captcha]);

  const onSubmitLogin = useCallback(
    (event) => {
      event.preventDefault();
      if (
        !normalizedEmail ||
        !password ||
        !isEmailValid ||
        (requireCaptcha && !captcha) ||
        loading
      ) {
        return;
      }
      onClickLogin();
    },
    [captcha, isEmailValid, loading, normalizedEmail, onClickLogin, password, requireCaptcha],
  );

  const onClickFindInvite = useCallback(() => {
    navigate('/lost-invite');
  }, []);

  useEffect(() => {
    if (error && captchaEl.current) {
      captchaEl.current.reset();
      setCaptcha('');
    }
  }, [error]);

  useEffect(() => {
    if (successMsg !== 'loading') {
      if (captchaEl.current) {
        captchaEl.current.reset();
        setCaptcha('');
      }
    }
  }, [successMsg]);

  if (token && currentUser && isPendingLogin2FA) {
    return (
      <Otp
        target={{
          email: currentUser.email,
          phone: currentUser.phone,
          mfa_method: currentUser.mfaMethod,
        }}
        onSuccess={() => {
          dispatch(refreshUser({ clearFlags: 'mfa' }));
        }}
        onBack={() => {
          dispatch(logOut());
        }}
      />
    );
  }

  return (
    <AuthLayoutContainer>
      <Box gap="24px" display="flex" flexDirection="column">
        <Box>
          <Typography variant="h2" style={{ color: colors.purple[900], fontWeight: '500' }}>
            Log in
          </Typography>

          <Typography variant="body1" style={{ color: colors.neutral[900], fontWeight: '400' }}>
            Log in to access your portal
          </Typography>
        </Box>

        <Box sx={{ border: '1px solid #F8F4F9', height: '0px' }} />

        {inviteAcceptedMessage ? (
          <Alert
            type="success"
            variant="contained"
            showBorder
            description={inviteAcceptedMessage}
            showClose={false}
          />
        ) : null}

        <Box
          component="form"
          display="flex"
          flexDirection="column"
          gap="24px"
          onSubmit={onSubmitLogin}
          noValidate
        >
          <Box display="flex" flexDirection="column">
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              autoComplete="email"
              minWidth="100%"
              label="Email"
              fullWidth
              size="medium"
              icon={<IconGraphy icon="Notifications.Email" />}
              errorType={!normalizedEmail || isEmailValid ? 'none' : 'error'}
              helperMessage={!isEmailValid && normalizedEmail ? 'Please enter a valid email' : ''}
              showCopy={false}
              showHelperOnTyping={false}
            />

            <Input
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type="password"
              autoComplete="current-password"
              minWidth="100%"
              fullWidth
              size="medium"
              icon={<IconGraphy icon="Security.Lock" />}
              showCopy={false}
              showHelperOnTyping={false}
              showHelperMessage={false}
            />

            {requireCaptcha && (
              <Box display="flex" justifyContent="flex-start" my={2}>
                <ReCAPTCHA
                  ref={(e) => (captchaEl.current = e)}
                  sitekey={'6LfBxuIpAAAAAANSty0dJjgR_zz0ZRknom5xSBNs'}
                  onChange={(value) => {
                    setCaptcha(value || '');
                  }}
                  onExpired={() => setCaptcha('')}
                />
              </Box>
            )}
          </Box>

          {error && (
            <Alert
              type="error"
              variant="contained"
              showBorder
              description={error}
              showClose={false}
            />
          )}

          <Typography
            onClick={onForgetPassword}
            style={{
              alignSelf: 'flex-end',
              textDecoration: 'underline',
              cursor: 'pointer',
              width: 'fit-content',
              userSelect: 'none',
              color: colors.purple[700],
            }}
          >
            Forgot your password?
          </Typography>

          <Button
            variant="primary"
            size="medium"
            type="submit"
            disabled={
              !normalizedEmail ||
              !password ||
              !isEmailValid ||
              (requireCaptcha && !captcha) ||
              loading
            }
            style={{ minWidth: '100%' }}
          >
            Log in
          </Button>
        </Box>

        <Alert
          type="warning"
          variant="contained"
          showBorder
          description="Can’t find your invitation?"
          showClose={false}
          cta={{
            label: 'Resend invitation',
            onClick: onClickFindInvite,
          }}
        />
      </Box>
    </AuthLayoutContainer>
  );
};
